import React, { useCallback, useState } from 'react';
import {
  Divider,
  Icon,
  SearchInput,
  SideNavItem,
  Tag,
  Text,
} from '@amzn/storm-ui';
import { spinner } from '@amzn/storm-ui-icons';
import { connect } from 'react-redux';
import { RootState } from 'src/app/store';
import { selectUserInfoFromHeader, UserInfo } from '../../store/slice';
import {
  DividerContainer,
  Loader,
  ResultsContainer,
  SearchContainer,
  SearchModal,
  TriggerButton,
  Wrapper,
} from './styles';
import {
  useDebouncedSearch,
  useKeyboardNavigation,
  useSearch,
  useSearchModal,
} from './hooks';

// Constants
const SEARCH_PLACEHOLDER = 'Search';

interface PageResult {
  page: string;
  title: string;
}

interface SearchBarProps {
  userInfo: UserInfo;
}

const SearchBar: React.FC<SearchBarProps> = (props: SearchBarProps) => {
  const [value, setValue] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [recommendedPages, setRecommendedPages] = useState<PageResult[]>([]);
  const [hasUsedLlmSearch, setHasUsedLlmSearch] = useState<boolean>(false);
  const locale = props.userInfo?.languageInfo?.currentLocale;

  // Custom hooks
  const { isOpen, setIsOpen, inputRef } = useSearchModal();
  const { keywordSearch: performKeywordSearch, llmSearch: performLlmSearch } =
    useSearch(locale);

  const keywordSearch = useCallback(
    async (query: string): Promise<void> => {
      const results = await performKeywordSearch(query);
      setRecommendedPages(results);
    },
    [performKeywordSearch],
  );

  const llmSearch = useCallback(
    async (query: string): Promise<void> => {
      setIsLoading(true);
      setHasUsedLlmSearch(true);
      try {
        const results = await performLlmSearch(query);
        setRecommendedPages(results);
      } finally {
        setIsLoading(false);
      }
    },
    [performLlmSearch],
  );

  // Event handlers
  const handlePageNavigation = useCallback((page: string) => {
    window.location.href = page;
  }, []);

  const handleClear = useCallback(() => {
    setValue('');
    setRecommendedPages([]);
    setHasUsedLlmSearch(false);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(e.target.value);
      // Reset LLM search flag when user starts typing again after using LLM search
      setHasUsedLlmSearch((prev) => {
        if (prev) {
          return false;
        }
        return prev;
      });
    },
    [],
  );

  const handleClearResults = useCallback(() => {
    setRecommendedPages([]);
  }, []);

  // Keyboard navigation
  const { selectedIndex, handleKeyDown } = useKeyboardNavigation(
    recommendedPages,
    handlePageNavigation,
    handleClose,
    llmSearch,
  );

  // Debounced search
  useDebouncedSearch(value, keywordSearch, handleClearResults);

  // Memoized results list for performance
  const resultsList = () => {
    if (recommendedPages.length === 0 || isLoading) return null;

    return (
      <ResultsContainer role="listbox" aria-label="Search suggestions">
        {recommendedPages.map((pageResult, index) => (
          <SideNavItem
            key={`AACChrome-${pageResult.page}-${index}`}
            isActive={selectedIndex === index}
            onClick={() => handlePageNavigation(pageResult.page)}
            role="option"
            aria-selected={selectedIndex === index}
          >
            {pageResult.title}
          </SideNavItem>
        ))}
      </ResultsContainer>
    );
  };

  return (
    <Wrapper>
      <TriggerButton type="button" onClick={() => setIsOpen(true)}>
        <SearchInput
          id="AACChrome-search-trigger"
          placeholder={SEARCH_PLACEHOLDER}
          hideLabel={true}
          fullWidth={true}
          onClear={handleClear}
          aria-label="Search trigger"
          readOnly
          tabIndex={-1}
        />
        <Tag label="Beta" />
      </TriggerButton>
      <SearchModal
        padding="small"
        isOpen={isOpen}
        role="dialog"
        aria-label="Search results"
        onClose={() => setIsOpen(false)}
        hasCloseButton={false}
      >
        <SearchContainer>
          <SearchInput
            id="AACChrome-search-input"
            placeholder={SEARCH_PLACEHOLDER}
            hideLabel={true}
            fullWidth={true}
            value={value}
            onChange={handleInputChange}
            onKeyDown={(e) => handleKeyDown(e, value)}
            onClear={handleClear}
            inputRef={inputRef}
            aria-label="Search input"
            aria-expanded={isOpen}
            aria-haspopup="listbox"
          />
          {isLoading && (
            <Loader role="status" aria-label="Loading search results">
              <Text size="large" weight="bold">
                {/* TODO: this label must be translated */}
                Loading results
              </Text>
              <Icon type={spinner} size="2x" color="default" />
            </Loader>
          )}
          {!hasUsedLlmSearch && (
            <DividerContainer>
              <Text size="small" color="tertiary">
                {/* TODO: this label must be translated */}
                Type and press Enter for full results
              </Text>
              <Divider />
            </DividerContainer>
          )}
          {resultsList()}
        </SearchContainer>
      </SearchModal>
    </Wrapper>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    userInfo: selectUserInfoFromHeader(state),
  };
};

export default connect(mapStateToProps)(SearchBar);
